import React, { useState, useEffect } from 'react';
import keycloak from './keycloak';
import { KeycloakTokenParsed } from 'keycloak-js';
import { useHistory, useLocation } from 'react-router-dom';
import ErrorMessage from './components/ErrorMessage';

/**
 * These next few interfaces roughly mirror the ones from firebase.auth
 */
interface UserInfo {
  displayName?: string | null;
  email?: string | null;
  photoURL?: string | null;
  /**
   * The groups this user belongs to. Used to determine if user
   * has access rights to various menus.
   */
  groups?: string[] | null;
  /**
   * The user's unique ID.
   */
  uid: string;
}

interface User extends UserInfo {
  isAnonymous: boolean;
}

type AuthContextValue = {
  accessToken?: string;
  userDetails?: User | null;
  signOut: () => void;
};

export const AuthContext = React.createContext<AuthContextValue>({ signOut: () => {} });

export interface AuthContextProps {
  authCtx: AuthContextValue;
}

const NotAuthenticatedMessage: React.FC = () => <ErrorMessage header='Villa!' message='Tókst ekki að auðkenni þig.' />;

type ParsedToken = KeycloakTokenParsed & {
  email?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  groups?: string[] | null;
};

function useQueryToken() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search).get('token'), [search]);
}

type ProviderProps = {
  children: React.ReactNode;
};

function Provider(props: ProviderProps) {
  const [authState, setAuthState] = useState<{
    initialized: boolean;
    parsedToken?: ParsedToken;
  }>({ initialized: false });

  const isInit = React.useRef(false);

  useEffect(() => {
    if (isInit.current) return;
    isInit.current = true;

    const initKeycloak = async () => {
      try {
        console.debug('init');
        const authenticated = await keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          checkLoginIframe: false,
        });

        if (authenticated) {
          setAuthState({
            initialized: true,
            parsedToken: keycloak.tokenParsed,
          });
        } else {
          console.debug('Not authenticated, redirecting to login');
          await keycloak.login();
        }
      } catch (e) {
        console.error('Error in auth initialization.', e);
        setAuthState({ initialized: false });
      }
    };

    initKeycloak();
  }, []);

  useEffect(() => {
    if (!authState.initialized) return;

    keycloak.onAuthRefreshSuccess = () => {
      console.debug('auth refresh success');
      setAuthState((prev) => ({
        ...prev,
        parsedToken: keycloak.tokenParsed,
      }));
    };

    keycloak.onTokenExpired = () => {
      console.debug('auth token expired');
      keycloak.updateToken(60).catch((err) => {
        console.error('Failed to refresh token on expiry', err);
      });
    };

    keycloak.onAuthRefreshError = () => {
      console.debug('auth refresh error');
      keycloak.login();
    };

    const refreshInterval = setInterval(() => {
      keycloak.updateToken(70).catch((err) => {
        console.error('Failed to proactively refresh token', err);
      });
    }, 30000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.debug('Tab became visible, checking token freshness');
        keycloak.updateToken(60).catch((err) => {
          console.error('Failed to refresh token on tab focus', err);
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return function cleanup() {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (keycloak) {
        keycloak.onAuthRefreshSuccess = undefined;
        keycloak.onTokenExpired = undefined;
        keycloak.onAuthRefreshError = undefined;
      }
    };
  }, [authState.initialized]);

  const ctx: AuthContextValue =
    authState.initialized && keycloak.authenticated && authState.parsedToken
      ? {
          accessToken: keycloak.token,
          signOut: () => {
            keycloak.logout();
          },
          userDetails: {
            displayName: authState.parsedToken.name ? authState.parsedToken.name : authState.parsedToken.preferred_username,
            groups: authState.parsedToken.groups ? authState.parsedToken.groups : [],
            isAnonymous: false,
            uid: authState.parsedToken.sub ? authState.parsedToken.sub : '',
          },
        }
      : {
          signOut: () => {},
        };

  return <AuthContext.Provider value={ctx}>{authState.parsedToken ? props.children : null}</AuthContext.Provider>;
}

type OverrideTokenProviderProps = {
  token: string;
} & ProviderProps;

function OverrideTokenProvider(props: OverrideTokenProviderProps) {
  try {
    const location = useLocation();
    const history = useHistory();

    const parsedToken = JSON.parse(atob(props.token.split('.')[1]));

    const ctx: AuthContextValue = {
      accessToken: props.token,
      signOut: () => {
        const q = new URLSearchParams(location.search);
        q.delete('token');
        history.push({ search: q.toString() });
        console.debug('signing out!');
      },
      userDetails: {
        displayName: parsedToken.claims
          ? parsedToken.claims.display_name
          : parsedToken.name
          ? parsedToken.name
          : parsedToken.preferred_username,
        isAnonymous: false,
        uid: parsedToken.sub ? parsedToken.sub : '',
      },
    };

    return <AuthContext.Provider value={ctx}>{props.children}</AuthContext.Provider>;
  } catch (e) {
    return (
      <AuthContext.Provider value={{ signOut: () => {} }}>
        <NotAuthenticatedMessage />
      </AuthContext.Provider>
    );
  }
}

type AuthWrapperProps = {
  loginMessage?: string;
  children: React.ReactNode;
};

function AuthWrapper(props: AuthWrapperProps) {
  // Override auth if token is provided as a query parameter
  const overrideToken = useQueryToken();

  if (overrideToken) {
    return <OverrideTokenProvider token={overrideToken}>{props.children}</OverrideTokenProvider>;
  }

  return <Provider>{props.children}</Provider>;
}

// Omit taken from https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-8.html
export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export function withAuth<P extends AuthContextProps>(Comp: React.ComponentType<P>): any {
  // eslint-disable-next-line react/display-name
  return (props: P) => <AuthContext.Consumer>{(value) => <Comp {...props} authCtx={value} />}</AuthContext.Consumer>;
}

export function withAnonymousAuth<P extends AuthContextProps>(Comp: React.ComponentType<P>): any {
  // eslint-disable-next-line react/display-name
  return (props: P) => <Comp {...props} authCtx={{}} anonymousAccess />;
}

export default AuthWrapper;

export { useQueryToken };
